import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bell, Plus, X, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ReminderItem({ reminder, onToggle, onDelete }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      data-testid={`reminder-item-${reminder.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: "0.55rem",
        padding: "0.3rem 0.5rem", borderRadius: "0.4rem",
        background: hovered ? "rgba(26,77,46,0.06)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      {/* Checkbox */}
      <button
        data-testid={`reminder-toggle-${reminder.id}`}
        onClick={() => onToggle(reminder)}
        style={{
          flexShrink: 0, width: "18px", height: "18px", borderRadius: "4px",
          border: `2px solid ${reminder.is_completed ? "#3d6b1f" : "rgba(26,77,46,0.3)"}`,
          background: reminder.is_completed ? "#3d6b1f" : "transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          padding: 0, transition: "all 0.15s",
        }}
      >
        {reminder.is_completed && (
          <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
            <path d="M1 3.5L3.5 6L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Text */}
      <span style={{
        flex: 1, fontFamily: "'Manrope',sans-serif", fontSize: "0.82rem",
        color: reminder.is_completed ? "#9ca3af" : "#374151",
        textDecoration: reminder.is_completed ? "line-through" : "none",
        transition: "color 0.2s",
        lineHeight: 1.4,
      }}>
        {reminder.text}
      </span>

      {/* Delete */}
      <button
        data-testid={`reminder-delete-${reminder.id}`}
        onClick={() => onDelete(reminder.id)}
        style={{
          flexShrink: 0, background: "none", border: "none", cursor: "pointer",
          color: hovered ? "#dc2626" : "#d1d5db",
          transition: "color 0.15s",
          padding: "0.1rem", display: "flex", alignItems: "center",
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export default function RemindersSection({ projectId, token }) {
  const [reminders, setReminders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/projects/${projectId}/reminders`,
          { headers: authHeaders }
        );
        setReminders(data);
      } catch { /* silent */ }
    };
    fetchReminders();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const sorted = [...reminders].sort((a, b) =>
    a.is_completed === b.is_completed ? 0 : a.is_completed ? 1 : -1
  );

  const handleAdd = async () => {
    const t = text.trim();
    if (!t) { setAdding(false); setText(""); return; }
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/reminders`,
        { text: t },
        { headers: authHeaders }
      );
      setReminders((prev) => [data, ...prev]);
      setText(""); // keep input open for rapid entry
    } catch { /* silent */ }
  };

  const handleToggle = async (reminder) => {
    // Optimistic update
    setReminders((prev) =>
      prev.map((r) => r.id === reminder.id ? { ...r, is_completed: !r.is_completed } : r)
    );
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/reminders/${reminder.id}/toggle`, {},
        { headers: authHeaders }
      );
      setReminders((prev) => prev.map((r) => r.id === data.id ? data : r));
    } catch {
      // Rollback
      setReminders((prev) => prev.map((r) => r.id === reminder.id ? reminder : r));
    }
  };

  const handleDelete = async (reminderId) => {
    setReminders((prev) => prev.filter((r) => r.id !== reminderId));
    try {
      await axios.delete(`${BACKEND_URL}/api/reminders/${reminderId}`, { headers: authHeaders });
    } catch { /* silent */ }
  };

  const clearCompleted = async () => {
    const done = reminders.filter((r) => r.is_completed);
    setReminders((prev) => prev.filter((r) => !r.is_completed));
    try {
      await Promise.all(
        done.map((r) => axios.delete(`${BACKEND_URL}/api/reminders/${r.id}`, { headers: authHeaders }))
      );
    } catch { /* silent */ }
  };

  const uncompleted = reminders.filter((r) => !r.is_completed);
  const completedCount = reminders.filter((r) => r.is_completed).length;

  return (
    <div
      data-testid="reminders-section"
      style={{
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.40)",
        padding: "0.55rem 1.5rem",
        flexShrink: 0,
      }}
    >
      {/* ── Header row ── */}
      <div
        onClick={() => setIsOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", userSelect: "none" }}
      >
        <Bell size={14} color="#3d6b1f" style={{ flexShrink: 0 }} />
        <span style={{ fontFamily: "'Manrope',sans-serif", color: "#1A4D2E", fontSize: "0.82rem", fontWeight: 700 }}>
          Reminders{uncompleted.length > 0 ? ` (${uncompleted.length})` : ""}
        </span>
        <span style={{ color: "#9ca3af", display: "flex", alignItems: "center", marginLeft: "0.1rem" }}>
          {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </span>

        {completedCount > 2 && isOpen && (
          <button
            onClick={(e) => { e.stopPropagation(); clearCompleted(); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: "'Manrope',sans-serif", fontSize: "0.72rem", color: "#9ca3af",
            }}
          >
            Clear {completedCount} done
          </button>
        )}

        <button
          data-testid="reminder-add-btn"
          onClick={(e) => { e.stopPropagation(); setIsOpen(true); setAdding(true); }}
          style={{
            marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.2rem",
            background: "rgba(26,77,46,0.07)", border: "1px solid rgba(26,77,46,0.15)",
            borderRadius: "2rem", padding: "0.2rem 0.65rem",
            fontFamily: "'Manrope',sans-serif", fontSize: "0.75rem", color: "#1A4D2E", fontWeight: 600,
            cursor: "pointer", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.14)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.07)")}
        >
          <Plus size={11} /> Add
        </button>
      </div>

      {/* ── Collapsible body ── */}
      {isOpen && (
        <>
          {/* Inline add input */}
          {adding && (
            <div style={{ marginTop: "0.4rem" }}>
              <input
                ref={inputRef}
                data-testid="reminder-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                  if (e.key === "Escape") { setAdding(false); setText(""); }
                }}
                onBlur={() => {
                  if (text.trim()) handleAdd();
                  else { setAdding(false); setText(""); }
                }}
                placeholder="Remind me to..."
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid rgba(26,77,46,0.2)",
                  borderRadius: "0.5rem", padding: "0.4rem 0.75rem",
                  fontFamily: "'Manrope',sans-serif", fontSize: "0.82rem", color: "#1A4D2E",
                  outline: "none",
                }}
              />
            </div>
          )}

          {/* Reminder list */}
          {sorted.length > 0 && (
            <div style={{
              marginTop: "0.4rem",
              maxHeight: "200px", overflowY: "auto",
              display: "flex", flexDirection: "column", gap: "0.1rem",
            }}>
              {sorted.map((r) => (
                <ReminderItem
                  key={r.id}
                  reminder={r}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {sorted.length === 0 && !adding && (
            <p style={{ fontFamily: "'Manrope',sans-serif", color: "#9ca3af", fontSize: "0.78rem", margin: "0.4rem 0 0", fontStyle: "italic" }}>
              No reminders yet — click Add to create one.
            </p>
          )}
        </>
      )}
    </div>
  );
}
